import { FunctionComponent } from 'react'
import { Box, Grid, FormControl, TextField, Button } from '@mui/material'
import { Cached, Search } from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers'
import styled from '@emotion/styled'
import { Controller, useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'

const StyledButton = styled(Button)({
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  borderRadius: '4px',
  height: '48px',
  padding: '14px',
  textDecoration: 'none',
  transition: 'all 0.4s ease 0s',
  '& svg': {
    color: '#fff',
    fontSize: '20px',
  },
})

interface Props {
  handleSearch: (values: NotificationFiltersType) => void
}

export type NotificationFiltersType = {
  orgName?: string
  until?: string
  from?: string
}

export const NotificationsFilters: FunctionComponent<Props> = ({ handleSearch }) => {
  const { t } = useTranslation()
  const {
    register,
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<NotificationFiltersType>({
    defaultValues: {
      orgName: '',
      until: '',
      from: '',
    },
  })

  const onSubmit = (values: Partial<NotificationFiltersType>) => {
    handleSearch(values)
  }

  const handleReset = () => {
    reset()
    handleSearch({
      orgName: '',
      from: '',
      until: '',
    })
  }

  return (
    <>
      <form id="notification-filter-form" onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            margin: '32px 0',
            position: 'relative',
          }}
        >
          <Grid
            container
            justifyContent="space-between"
            rowGap="10px"
            columnGap={{ xs: 0, md: '10px' }}
            sx={{
              padding: '20px',
              border: '1px solid #e1e4e8',
              borderRadius: '4px',
            }}
          >
            <Grid
              item
              xs={9}
              md={'auto'}
              sx={{
                flexGrow: '1 !important',
              }}
            >
              <TextField
                id="search"
                placeholder={t('tender-organization-name')}
                variant="outlined"
                fullWidth
                sx={{
                  maxHeight: '48px',
                }}
                {...register('orgName')}
              />
            </Grid>
            <Grid item xs={12} md={'auto'} order={{ xs: 1, md: 0 }}>
              <Box display="flex" gap="10px">
                <FormControl sx={{ width: '50%' }}>
                  <Controller
                    name="from"
                    control={control}
                    render={({
                      field: { ref, onBlur, name, onChange, ...field },
                      fieldState,
                    }) => (
                      <DatePicker
                        {...field}
                        inputRef={ref}
                        value={field.value ? field.value : null}
                        onChange={(val) => {
                          onChange(dayjs(val).format('YYYY-MM-DD'))
                        }}
                        slotProps={{
                          textField: {
                            placeholder: t('from'),
                            onBlur,
                            name,
                            error: !!fieldState?.error,
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
                <FormControl sx={{ width: '50%' }}>
                  <Controller
                    name="until"
                    control={control}
                    render={({
                      field: { ref, onBlur, name, onChange, ...field },
                      fieldState,
                    }) => (
                      <DatePicker
                        {...field}
                        inputRef={ref}
                        value={field.value ? field.value : null}
                        onChange={(val) => {
                          onChange(dayjs(val).format('YYYY-MM-DD'))
                        }}
                        slotProps={{
                          textField: {
                            placeholder: t('to'),
                            onBlur,
                            name,
                            error: !!fieldState?.error,
                          },
                        }}
                      />
                    )}
                  />
                </FormControl>
              </Box>
            </Grid>
            <Grid item xs={3} md={'auto'}>
              <Box display="flex" justifyContent="flex-end" gap="10px">
                <StyledButton
                  sx={{
                    background: 'rgb(122, 127, 126)',
                    '&:hover': {
                      background: 'rgb(135, 141, 140)',
                    },
                  }}
                  type="submit"
                  form="notification-filter-form"
                >
                  <Search />
                </StyledButton>
                <StyledButton
                  sx={{
                    background: 'rgb(3, 34, 76)',
                    color: '#fff',
                    '&:hover': {
                      background: 'rgba(3, 34, 76, 0.6)',
                    },
                  }}
                  onClick={handleReset}
                >
                  <Cached />
                </StyledButton>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </form>
    </>
  )
}
